import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import DataTable from "react-data-table-component";
import { customTableStyles } from "../../assets/CustomTableStyles";
import { get } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

// Utility function to format date as DD-MM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Utility function to format date as DD-MMM
const formatShortDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("default", { month: "short" });
  return `${day}-${month}`;
};

function AttendanceReport() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      classIds: [],
      classSectionIds: [],
      studentIds: [],
      fromDate: "",
      toDate: "",
    },
    mode: "onChange",
  });

  const selectedClasses = useWatch({ control, name: "classIds" });
  const selectedSections = useWatch({ control, name: "classSectionIds" });
  const fromDate = useWatch({ control, name: "fromDate" });
  const toDate = useWatch({ control, name: "toDate" });
  const selectedStudents = useWatch({ control, name: "studentIds" });

  const [classesData, setClassesData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [studentsData, setStudentsData] = useState([]);
  const [reportData, setReportData] = useState([]);

  // Dynamic validation: studentIds required only for parents
  useEffect(() => {
    const isParent = roleId === "9" || roleId === "10";
    if (isParent) {
      trigger("studentIds");
    }
  }, [selectedStudents, roleId, trigger]);

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setClassesData(response || []);
      } catch (error) {
        console.error("Error fetching classes:", error);
        toast.error("Failed to fetch classes.");
      }
    };
    if (token && centerId) fetchClasses();
  }, [token, centerId]);

  // Fetch sections when selected classes change
  useEffect(() => {
    const classIds = selectedClasses?.map((c) => c.value) || [];
    if (classIds.length > 0) {
      const fetchSections = async () => {
        try {
          // Fetch sections for all selected class IDs
          const promises = classIds.map((id) =>
            get(
              `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${id}&centerId=${centerId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
          );
          const results = await Promise.all(promises);
          const allSections = results.flat();
          setClassSectionsData(allSections || []);
        } catch (error) {
          console.error("Error fetching sections:", error);
          toast.error("Failed to fetch sections.");
        }
      };
      fetchSections();
    } else if (roleId !== "9" && roleId !== "10") {
      setClassSectionsData([]);
      setValue("classSectionIds", []);
      setValue("studentIds", []);
      setStudentsData([]);
    }
  }, [selectedClasses, token, centerId, setValue, roleId]);

  // Auto-fetch parent's children when centerId is available
  useEffect(() => {
    if ((roleId === "9" || roleId === "10") && centerId) {
      // Just trigger student fetch — don't touch form
    }
  }, [centerId, roleId]);

  // Fetch students when selected sections change
  useEffect(() => {
    const sectionIds = selectedSections?.map((s) => s.value) || [];
    const isParent = roleId === "9" || roleId === "10";

    if ((sectionIds.length > 0 && !isParent) || (isParent && centerId)) {
      const fetchStudents = async () => {
        try {
          let promises = [];
          let userIdToUse = isParent ? userId : "0";

          if (isParent) {
            // Parent: fetch their own children using classSectionId = 0
            promises = [
              get(
                `Student/GetStudentByClassSectionIdAsync?classSectionId=0&userId=${userIdToUse}`,
                { headers: { Authorization: `Bearer ${token}` } }
              ),
            ];
          } else {
            // Non-parent: fetch by selected sections
            promises = sectionIds.map((id) =>
              get(
                `Student/GetStudentByClassSectionIdAsync?classSectionId=${id}&userId=${userIdToUse}`,
                { headers: { Authorization: `Bearer ${token}` } }
              )
            );
          }

          const results = await Promise.all(promises);
          const allStudents = results.flat();
          setStudentsData(allStudents || []);
        } catch (error) {
          console.error("Error fetching students:", error);
          toast.error("Failed to fetch students.");
        }
      };
      fetchStudents();
    } else if (roleId !== "9" && roleId !== "10") {
      setStudentsData([]);
      setValue("studentIds", []);
    }
  }, [selectedSections, roleId, centerId, userId, token, setValue]);

  // Fetch report data
  const fetchReport = async (data) => {
    try {
      const isParent = roleId === "9" || roleId === "10";

      const classIds = isParent
        ? ""
        : data.classIds?.map((c) => c.value).join(",") || "";

      const classSectionIds = isParent
        ? ""
        : data.classSectionIds?.map((s) => s.value).join(",") || "";

      const studentIds = data.studentIds?.map((st) => st.value).join(",") || "";

      const endpoint = `CenterAttendence/GetAttendanceReport?classIds=${classIds}&classSectionIds=${classSectionIds}&studentIds=${studentIds}&FromDate=${data.fromDate}&ToDate=${data.toDate}&centerId=${centerId}`;

      const response = await get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response && response.length) {
        setReportData(response);
      } else {
        setReportData([]);
        toast.info("No attendance records found for the selected criteria.");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setReportData([]);
      toast.error("An error occurred while fetching the report.");
    }
  };

  // Helper to format date safely in LOCAL time
  const getLocalDateKey = (dateStr) => {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Collect unique local dates
  const allDates = useMemo(
    () =>
      Array.from(
        new Set(
          reportData.flatMap((student) =>
            student.attendanceReportDetails.map((d) =>
              getLocalDateKey(d.dayDate)
            )
          )
        )
      ).sort(),
    [reportData]
  );

  // Transform data for pivot table
  const transformedData = useMemo(
    () =>
      reportData.map((student) => {
        const row = { name: student.name, rollNo: student.rollNo };
        allDates.forEach((dateKey) => {
          const attn = student.attendanceReportDetails.find(
            (d) => getLocalDateKey(d.dayDate) === dateKey
          );
          row[dateKey] = attn ? attn.attnType : "-";
        });
        return row;
      }),
    [reportData, allDates]
  );

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return transformedData;
    const lower = searchTerm.toLowerCase();
    return transformedData.filter(
      (row) =>
        row.name.toLowerCase().includes(lower) ||
        row.rollNo.toLowerCase().includes(lower) ||
        allDates.some((date) => (row[date] || "").toLowerCase().includes(lower))
    );
  }, [searchTerm, transformedData, allDates]);

  // Build dynamic DataTable columns
  const columns = useMemo(
    () => [
      {
        name: "Sr No.",
        cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
        sortable: false,
        width: "60px",
      },
      {
        name: "Pupil",
        cell: (row) => row.rollNo + " - " + row.name,
        selector: (row) => row.name,
        sortable: true,
        wrap: true,
        width: "200px",
      },
      ...allDates.map((date) => ({
        name: formatShortDate(date),
        selector: (row) => row[date],
        cell: (row) => (
          <span
            className={`mark-box ${
              row[date] === "P"
                ? "present"
                : row[date] === "L"
                ? "late-unauthorized"
                : row[date] === "I"
                ? "late-authorized"
                : row[date] === "S"
                ? "absent-approved"
                : row[date] === "T"
                ? "absent-travel"
                : row[date] === "M"
                ? "absent-medical"
                : row[date] === "O"
                ? "absent-not-authorized"
                : row[date] === "A"
                ? "absent-authorized"
                : row[date] === "B"
                ? "early-dismissal-unauthorized"
                : row[date] === "U"
                ? "early-dismissal-authorized"
                : row[date] === "E"
                ? "early-dismissal-unauthorized"
                : row[date] === "D"
                ? "early-dismissal-authorized"
                : ""
            }`}
          >
            {row[date]}
          </span>
        ),
        sortable: false,
        width: "90px",
      })),
    ],
    [allDates, currentPage, rowsPerPage]
  );

  // Date validations
  const today = new Date().toISOString().split("T")[0];
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const minDate = oneYearAgo.toISOString().split("T")[0];

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredData.flatMap((row, index) => {
      const baseRow = {
        "Sr No": String((currentPage - 1) * rowsPerPage + index + 1),
        "Student Name": row.name,
        "Roll No": row.rollNo,
      };
      allDates.forEach((date) => {
        baseRow[formatShortDate(date)] = row[date] || "-";
      });
      return [baseRow];
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Report");
    XLSX.writeFile(
      workbook,
      `Attendance_Report_${formatDate(fromDate)}_to_${formatDate(toDate)}.xlsx`
    );
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
    const title = `Attendance Report (${formatDate(fromDate)} to ${formatDate(
      toDate
    )})`;
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);
    const headers = [
      "Sr No",
      "Student Name",
      "Roll No",
      ...allDates.map((date) => formatShortDate(date)),
    ];
    const bodyData = filteredData.map((row, index) => [
      String((currentPage - 1) * rowsPerPage + index + 1),
      row.name,
      row.rollNo,
      ...allDates.map((date) => row[date] || "-"),
    ]);
    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [144, 84, 255],
        textColor: [255, 255, 255],
      },
      styles: {
        cellWidth: "auto",
        minCellWidth: 20,
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
      },
    });
    doc.save(
      `Attendance_Report_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`
    );
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Attendance Report</h2>
      </div>
      <div className="card">
        <div className="card-body">
          <form
            className="row g-2 new-form-style"
            onSubmit={handleSubmit((data) => {
              // Validate required fields
              if (!data.fromDate || !data.toDate) {
                toast.error("From Date and To Date are required.");
                return;
              }
              if (
                (roleId === "9" || roleId === "10") &&
                (!data.studentIds || data.studentIds.length === 0)
              ) {
                toast.error("Please select a pupil.");
                return;
              }
              fetchReport(data);
            })}
          >
            {/* Year Group - Only for non-parents */}
            {roleId !== "9" && roleId !== "10" && (
              <div className="col-md-2">
                <label className="form-label form-label-new">Class</label>
                <Controller
                  name="classIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isMulti
                      options={classesData.map((classItem) => ({
                        value: classItem.id,
                        label: classItem.name,
                      }))}
                      onChange={(selected) => {
                        field.onChange(selected || []);
                        setValue("classSectionIds", []);
                        setValue("studentIds", []);
                        setClassSectionsData([]);
                        setStudentsData([]);
                      }}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                      isClearable
                      placeholder="All"
                    />
                  )}
                />
              </div>
            )}

            {/* Year Group Form - Only for non-parents */}
            {roleId !== "9" && roleId !== "10" && (
              <div className="col-md-2">
                <label className="form-label form-label-new">Section</label>
                <Controller
                  name="classSectionIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isMulti
                      options={classSectionsData.map((section) => ({
                        value: section.id,
                        label: section.name,
                      }))}
                      onChange={(selected) => {
                        field.onChange(selected || []);
                        setValue("studentIds", []);
                        setStudentsData([]);
                      }}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                      isClearable
                      isDisabled={
                        !selectedClasses || selectedClasses.length === 0
                      }
                      placeholder="All"
                    />
                  )}
                />
              </div>
            )}

            <div className="col-md-2">
              <label className="form-label form-label-new">
                Pupil
                {(roleId === "9" || roleId === "10") && (
                  <span className="text-danger fs-9">*</span>
                )}
              </label>
              <Controller
                name="studentIds"
                control={control}
                rules={{
                  required:
                    roleId === "9" || roleId === "10"
                      ? "Pupil is required."
                      : false,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    options={studentsData.map((student) => ({
                      value: student.id,
                      label: student.studentName,
                    }))}
                    onChange={(selected) => field.onChange(selected || [])}
                    className="react-select-style"
                    classNamePrefix="react-select-style-pref"
                    isClearable
                    isDisabled={studentsData.length === 0}
                  />
                )}
              />
              {errors.studentIds && (
                <small className="text-danger">
                  {errors.studentIds.message}
                </small>
              )}
            </div>

            <div className="col-md-2">
              <label className="form-label form-label-new">
                From Date<span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("fromDate", {
                  required: "This field is required.",
                  validate: {
                    notFuture: (value) =>
                      value <= today || "From date cannot be in the future.",
                    withinRange: (value) =>
                      value >= minDate ||
                      "From date must be within the last year.",
                    beforeToDate: (value) =>
                      !toDate ||
                      value <= toDate ||
                      "From date must be before or equal to To date.",
                  },
                })}
                type="date"
                className="form-control"
                max={today}
                min={minDate}
              />
              {errors?.fromDate && (
                <small className="text-danger">{errors.fromDate.message}</small>
              )}
            </div>

            <div className="col-md-2">
              <label className="form-label form-label-new">
                To Date<span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("toDate", {
                  required: "This field is required.",
                  validate: {
                    notFuture: (value) =>
                      value <= today || "To date cannot be in the future.",
                    withinRange: (value) =>
                      value >= minDate ||
                      "To date must be within the last year.",
                    afterFromDate: (value) =>
                      !fromDate ||
                      value >= fromDate ||
                      "To date must be after or equal to From date.",
                  },
                })}
                type="date"
                className="form-control"
                max={today}
                min={fromDate || minDate}
              />
              {errors?.toDate && (
                <small className="text-danger">{errors.toDate.message}</small>
              )}
            </div>

            <div className="col-1">
              <button
                className="btn btn-primary w-100"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span
                    className="spinner-border spinner-custom-size me-1"
                    aria-hidden="true"
                  ></span>
                ) : null}
                <span role="status">Fetch</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {reportData.length > 0 ? (
        <div className="card mt-3">
          <div className="card-body">
            <div
              className="d-flex justify-content-end mb-1"
              style={{ marginTop: 0 }}
            >
              <div className="d-flex align-items-start gap-1">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Pupil Name, Roll No, or Status"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: "200px" }}
                />
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to Excel"
                  className="btn btn-outline-success px-2 py-1"
                  onClick={exportToExcel}
                >
                  <i className="fs-8 fa fa-file-excel"></i>
                </button>
              </div>
            </div>
            <div className="table-container fixed-second-column-table">
              <DataTable
                columns={columns}
                data={filteredData}
                customStyles={customTableStyles}
                pagination
                paginationRowsPerPageOptions={[
                  10, 25, 50, 100, 200, 300, 400, 500,
                ]}
                paginationDefaultPage={1}
                paginationPerPage={rowsPerPage}
                dense
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={setRowsPerPage}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="card mt-3">
          <div className="card-body text-center">
            There are no records to display
          </div>
        </div>
      )}
    </>
  );
}

export default AttendanceReport;
